import {
  GenerateInvoiceListItem,
  GenerateInvoiceSubRow,
  GenerateInvoiceSubRowType,
} from '../../types';

/**
 * Row ids for the generate-invoices table are built ONLY by the three functions below.
 *
 * TanStack derives them through `getInvoiceRowId`, while the page and the custom-project hook
 * have to predict the id of a row that does not exist yet — they queue it so the row gets
 * selected once the table data rebuilds. Both sides must produce byte-identical ids: a literal
 * that drifts here silently stops selecting newly added rows rather than failing loudly.
 */
export const getInvoiceProjectRowId = (projectId: number | string): string =>
  `project-${projectId}`;

/** Id of a sub-row hanging off the project row `projectRowId`. */
export const getInvoiceSubRowId = (projectRowId: string, subRow: GenerateInvoiceSubRow): string => {
  switch (subRow.type) {
    case GenerateInvoiceSubRowType.ADDED_MEMBER:
      return `${projectRowId}-added-member-${subRow.id}`;
    case GenerateInvoiceSubRowType.OVERHEAD:
      return `${projectRowId}-overhead-${subRow.id}`;
    case GenerateInvoiceSubRowType.ADD_OVERHEAD:
      return `${projectRowId}-add-overhead`;
    case GenerateInvoiceSubRowType.MEMBER:
      return `${projectRowId}-member-${subRow.id}`;
    default: {
      // Every sub-row type is spelled out above so a new one fails the build here rather than
      // silently inheriting another type's id shape.
      const _exhaustive: never = subRow;
      throw new Error(`Unhandled GenerateInvoiceSubRowType: ${_exhaustive}`);
    }
  }
};

/**
 * Row identity for the table. Sub-rows are keyed by their own stable id (a UUID for user-added
 * rows) rather than array position, so selection and form paths survive re-ordering — and so the
 * same member can legitimately appear twice under one project.
 */
export const getInvoiceRowId = (
  row: GenerateInvoiceListItem,
  _index: number,
  parent?: { id: string }
): string =>
  parent
    ? getInvoiceSubRowId(parent.id, row as unknown as GenerateInvoiceSubRow)
    : getInvoiceProjectRowId(row.id);
